import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { DollarSign, CheckCircle, TrendingUp, Clock, ChevronDown } from 'lucide-react'
import { payoutsService } from '@/services/payoutsService'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatCurrency, formatRelativeTime, getLocalizedField } from '@/utils/formatters'

const STATUS_TABS = [
  { key: '',        label: 'الكل',        color: '#767683' },
  { key: 'pending', label: 'قيد الانتظار', color: '#FF6D00' },
  { key: 'paid',    label: 'تم الدفع',    color: '#1B6D24' },
]

function SummaryCard({ icon: Icon, label, value, color, isCurrency }) {
  return (
    <div className="hg-card p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}12`, color }}>
        <Icon size={22} />
      </div>
      <div>
        <div className="font-black text-2xl text-navy" style={{ fontFamily: 'Cairo, sans-serif' }}>
          {isCurrency ? formatCurrency(value ?? 0) : (value ?? '—')}
        </div>
        <div className="text-sm text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</div>
      </div>
    </div>
  )
}

export default function PayoutsPage() {
  const qc = useQueryClient()
  const { page, limit, setPage } = usePagination(1, 15)
  const [activeTab, setActiveTab] = useState('')
  const [approveDialog, setApproveDialog] = useState({ open: false, id: null, vendor: '' })

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['payouts-admin', { status: activeTab, page, limit }],
    queryFn: () => payoutsService.getAll({
      status: activeTab || undefined,
      page,
      limit,
    }),
    retry: 1,
  })

  const { data: summaryResp } = useQuery({
    queryKey: ['commissions-summary'],
    queryFn: payoutsService.getSummary,
    retry: 1,
  })

  const summary  = summaryResp?.summary || {}
  const byVendor = summaryResp?.byVendor || []

  const payouts = data?.data || []
  const meta    = data?.meta || {}

  const approveMut = useMutation({
    mutationFn: ({ id, transactionId, documentNumber }) => payoutsService.approve(id, { transactionId, documentNumber }),
    onSuccess: () => {
      toast.success('تم تأكيد الدفع بنجاح')
      qc.invalidateQueries({ queryKey: ['payouts-admin'] })
      qc.invalidateQueries({ queryKey: ['commissions-summary'] })
      setApproveDialog({ open: false, id: null, vendor: '' })
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل تأكيد الدفع'),
  })

  return (
    <div className="space-y-5">

      {/* Header */}
      <div>
        <h1 className="font-black text-navy text-xl" style={{ fontFamily: 'Cairo, sans-serif' }}>العمولات والمدفوعات</h1>
        <p className="text-sm text-ghost mt-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>
          متابعة مستحقات البائعين وتأكيد المدفوعات
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard
          icon={TrendingUp}
          label="إجمالي العمولات المحصلة"
          value={summary.totalEarned}
          color="#1B6D24"
          isCurrency
        />
        <SummaryCard
          icon={DollarSign}
          label="صافي مستحقات البائعين"
          value={summary.totalNet}
          color="#0077B6"
          isCurrency
        />
        <SummaryCard
          icon={Clock}
          label="مستحقات قيد الانتظار"
          value={summary.totalPending}
          color="#FF6D00"
          isCurrency
        />
        <SummaryCard
          icon={CheckCircle}
          label="إجمالي المدفوع للبائعين"
          value={summary.totalPaid}
          color="#000666"
          isCurrency
        />
      </div>

      {/* Filters */}
      <div className="hg-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setPage(1) }}
              className="px-4 py-2 rounded-xl text-xs font-bold transition-all"
              style={{
                background: activeTab === tab.key ? tab.color : `${tab.color}12`,
                color: activeTab === tab.key ? '#fff' : tab.color,
                fontFamily: 'Tajawal, sans-serif',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {meta.total != null && (
          <span className="text-xs font-bold px-3 py-1.5 rounded-xl" style={{ background: 'rgba(0,6,102,0.06)', color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
            {meta.total} سجل
          </span>
        )}
      </div>

      {/* Table */}
      <div className="hg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p style={{ color: '#BA1A1A', fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>فشل تحميل المدفوعات</p>
            <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button>
          </div>
        ) : payouts.length === 0 ? (
          <EmptyState icon={DollarSign} message="لا توجد سجلات مدفوعات بهذه المعايير" />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9F9F9', borderBottom: '2px solid #F3F3F3' }}>
                    {['البائع', 'إجمالي المبيعات', 'نسبة العمولة', 'المبلغ المستحق', 'الحالة', 'التاريخ', 'الإجراءات'].map((h) => (
                      <th key={h} style={{ textAlign: 'right', padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#767683', whiteSpace: 'nowrap', fontFamily: 'Tajawal, sans-serif' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout, idx) => {
                    const isPending = payout.status === 'pending'
                    const vendorName = getLocalizedField(payout.vendor?.storeName, 'ar') || payout.vendor?.storeName || '—'
                    return (
                      <tr key={payout.id} style={{ borderBottom: idx === payouts.length - 1 ? 'none' : '1px solid #F3F3F3' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#F9F9F9'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>

                        {/* Vendor */}
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#000666', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, fontFamily: 'Cairo, sans-serif', flexShrink: 0 }}>
                              {vendorName.charAt(0)}
                            </div>
                            <div>
                              <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#1A1C1C' }}>{vendorName}</div>
                              <div style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>{payout.vendor?.user?.email || ''}</div>
                            </div>
                          </div>
                        </td>

                        {/* Sales */}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13 }}>{formatCurrency(payout.grossAmount || 0)}</span>
                        </td>

                        {/* Commission Rate */}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#FF6D00' }}>
                            {payout.commissionRate ?? payout.vendor?.commissionRate ?? '—'}%
                          </span>
                        </td>

                        {/* Net amount owed to the vendor */}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 15, color: '#1B6D24' }}>
                            {formatCurrency(payout.netAmount || 0)}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: isPending ? 'rgba(255,109,0,0.1)' : 'rgba(27,109,36,0.1)',
                            color: isPending ? '#FF6D00' : '#1B6D24',
                            fontFamily: 'Tajawal, sans-serif',
                          }}>
                            {isPending ? 'قيد الانتظار' : 'تم الدفع'}
                          </span>
                        </td>

                        {/* Date */}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>
                            {formatRelativeTime(payout.createdAt)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '12px 14px' }}>
                          {isPending && (
                            <button
                              onClick={() => setApproveDialog({ open: true, id: payout.id, vendor: vendorName })}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '6px 12px', borderRadius: 10,
                                background: 'rgba(27,109,36,0.08)', color: '#1B6D24',
                                border: '1px solid rgba(27,109,36,0.2)', cursor: 'pointer',
                                fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif',
                              }}
                            >
                              <CheckCircle size={13} />
                              تأكيد الدفع
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '0 16px 16px' }}>
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      {/* Per-vendor breakdown */}
      {byVendor.length > 0 && (
        <div className="hg-card p-5">
          <h2 className="font-black text-navy mb-4" style={{ fontFamily: 'Cairo, sans-serif', fontSize: 15 }}>
            ملخص لكل بائع
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #F3F3F3' }}>
                  {['البائع', 'إجمالي المبيعات', 'العمولة المحصلة', 'صافي البائع', 'قيد الانتظار', 'تم الدفع'].map((h) => (
                    <th key={h} style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byVendor.map((v, i) => (
                  <tr key={v.vendor?.id || i} style={{ borderBottom: i === byVendor.length - 1 ? 'none' : '1px solid #F9F9F9' }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13 }}>
                      {getLocalizedField(v.vendor?.storeName, 'ar') || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontFamily: 'Cairo, sans-serif', fontWeight: 700 }}>{formatCurrency(v.grossAmount || 0)}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'Cairo, sans-serif', fontWeight: 700, color: '#FF6D00' }}>{formatCurrency(v.commissionEarned || 0)}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'Cairo, sans-serif', fontWeight: 700, color: '#0077B6' }}>{formatCurrency(v.netToVendor || 0)}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'Cairo, sans-serif', fontWeight: 700, color: '#FF6D00' }}>{formatCurrency(v.pendingAmount || 0)}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'Cairo, sans-serif', fontWeight: 700, color: '#1B6D24' }}>{formatCurrency(v.paidAmount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {approveDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={() => setApproveDialog({ open: false, id: null, vendor: '', transactionId: '', documentNumber: '' })}>
          <div className="hg-card p-6 w-full max-w-sm animate-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-black text-ink text-base mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>تأكيد الدفع للبائع</h3>
            <p className="text-sm text-dim mb-4" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              هل تريد تأكيد دفع المستحقات لـ "{approveDialog.vendor}"؟ يرجى إدخال بيانات التحويل للمتابعة.
            </p>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>رقم العملية (Transaction ID) *</label>
                <input 
                  type="text" 
                  className="hg-input" 
                  value={approveDialog.transactionId || ''} 
                  onChange={(e) => setApproveDialog(p => ({ ...p, transactionId: e.target.value }))}
                  placeholder="مثال: TRX-123456"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>رقم المستند (Document Number) *</label>
                <input 
                  type="text" 
                  className="hg-input" 
                  value={approveDialog.documentNumber || ''} 
                  onChange={(e) => setApproveDialog(p => ({ ...p, documentNumber: e.target.value }))}
                  placeholder="مثال: DOC-789012"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => approveMut.mutate({ id: approveDialog.id, transactionId: approveDialog.transactionId, documentNumber: approveDialog.documentNumber })}
                disabled={!approveDialog.transactionId || !approveDialog.documentNumber || approveMut.isPending}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white transition-all"
                style={{ background: '#1B6D24', fontFamily: 'Cairo, sans-serif', opacity: (!approveDialog.transactionId || !approveDialog.documentNumber || approveMut.isPending) ? 0.6 : 1 }}
              >
                {approveMut.isPending ? 'جاري التأكيد...' : 'تأكيد الدفع'}
              </button>
              <button
                onClick={() => setApproveDialog({ open: false, id: null, vendor: '', transactionId: '', documentNumber: '' })}
                disabled={approveMut.isPending}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all bg-gray-100 text-gray-700"
                style={{ fontFamily: 'Cairo, sans-serif' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
